import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useEvents } from '../../context/EventsContext';
import { formatDate, formatPrice, eventStatuses, mediaUrl } from '../../utils/format';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Calendar, Plus, Edit, Trash2, LogOut,
  LayoutGrid, Eye, Users, Timer, ScanLine, CreditCard,
  CheckCircle2, XCircle, ChevronRight, Copy, Send, EyeOff, FileText
} from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { admin, logout } = useAuth();
  const {
    events, fetchAdminEvents, deleteEvent, cloneEvent, publishEvent, unpublishEvent,
    getPendingReservations, confirmReservation, rejectReservation,
  } = useEvents();
  const [pendingRes, setPendingRes] = useState([]);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [busyEventId, setBusyEventId] = useState(null);

  useEffect(() => {
    fetchAdminEvents();
    (async () => {
      try {
        const data = await getPendingReservations();
        setPendingRes(data || []);
      } catch { /* ignore */ }
    })();
  }, [fetchAdminEvents, getPendingReservations]);

  const handleLogout = () => { logout(); navigate('/admin'); };

  const handleDelete = async (eventId, eventName) => {
    if (window.confirm(`¿Estás seguro de eliminar "${eventName}"?`)) {
      await deleteEvent(eventId);
    }
  };

  const handleClone = async (eventId) => {
    setBusyEventId(eventId);
    try {
      const newEv = await cloneEvent(eventId);
      navigate(`/admin/events/${newEv.id}/edit`);
    } catch (err) {
      window.alert('No se pudo duplicar el evento: ' + err.message);
    } finally {
      setBusyEventId(null);
    }
  };

  const handlePublish = async (eventId) => {
    setBusyEventId(eventId);
    try { await publishEvent(eventId); }
    finally { setBusyEventId(null); }
  };

  const handleUnpublish = async (eventId) => {
    setBusyEventId(eventId);
    try { await unpublishEvent(eventId); }
    finally { setBusyEventId(null); }
  };

  const stats = {
    totalEvents: events.length,
    activeEvents: events.filter(e => e.status === 'open').length,
    drafts: events.filter(e => e.status === 'draft').length,
    totalChairs: events.reduce((sum, e) => sum + (e.totalChairs || 0), 0),
    availableChairs: events.reduce((sum, e) => sum + (e.availableChairs || 0), 0),
    pendingCount: pendingRes.length,
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="border-b border-border bg-secondary/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <h1 className="text-xl sm:text-2xl font-serif font-bold text-primary">Reserva</h1>
            <Badge variant="outline" className="border-primary/50 text-primary hidden sm:inline-flex">Admin</Badge>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <span className="text-xs sm:text-sm text-muted-foreground hidden sm:inline truncate max-w-[180px]">{admin?.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-muted-foreground hover:text-foreground">
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Salir</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <StatCard icon={<Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />} bg="bg-primary/10" value={stats.totalEvents} label="Eventos" />
          <StatCard icon={<Eye className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-500" />} bg="bg-emerald-500/10" value={stats.activeEvents} label="Activos" />
          <StatCard icon={<FileText className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-400" />} bg="bg-zinc-500/10" value={stats.drafts} label="Borradores" />
          <StatCard icon={<Users className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500" />} bg="bg-amber-500/10" value={`${stats.availableChairs}/${stats.totalChairs}`} label="Sillas disp." />
          <StatCard icon={<Timer className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500" />} bg="bg-amber-500/10" value={stats.pendingCount} label="Por confirmar" />
        </div>

        {/* Pending Reservations Section */}
        {pendingRes.length > 0 && (
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center gap-2 mb-3 sm:mb-4">
              <Timer className="w-5 h-5 text-amber-400" />
              <h2 className="text-base sm:text-lg font-serif font-semibold text-foreground">Reservas Pendientes</h2>
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">{pendingRes.length}</Badge>
            </div>
            <div className="space-y-2">
              {pendingRes.map(res => {
                const resChairs = res.chairs || [];
                const total = resChairs.reduce((s, c) => s + (parseFloat(c.price) || 0), 0);
                return (
                  <div key={res.id} className="bg-secondary border border-amber-500/20 rounded-lg p-3 sm:p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="w-9 h-9 rounded-full bg-amber-500/10 flex items-center justify-center shrink-0">
                          <Timer className="w-4 h-4 text-amber-400" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-foreground truncate">{res.full_name}</p>
                          <p className="text-xs text-muted-foreground truncate">{res.email} · {resChairs.length} silla{resChairs.length !== 1 ? 's' : ''} · {formatPrice(total)}</p>
                          <p className="text-xs text-muted-foreground/60 truncate">{res.paymentMethod} · {res.eventName} · {new Date(res.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                          {res.paymentProofUrl && (
                            <a
                              href={mediaUrl(res.paymentProofUrl)}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`pending-proof-${res.id}`}
                              className="inline-flex items-center gap-1 mt-1 text-xs text-primary hover:underline"
                            >
                              <FileText className="w-3 h-3" /> Ver comprobante
                            </a>
                          )}
                        </div>
                      </div>
                      {rejectingId === res.id ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <input
                            type="text"
                            value={rejectReason}
                            onChange={e => setRejectReason(e.target.value)}
                            placeholder="Motivo del rechazo..."
                            className="h-8 px-3 text-sm rounded border border-border bg-background text-foreground flex-1 min-w-[140px] sm:w-48"
                          />
                          <Button size="sm" className="h-8 bg-red-600 hover:bg-red-700" disabled={!rejectReason.trim()}
                            onClick={async () => {
                              await rejectReservation(res.id, rejectReason.trim());
                              setPendingRes(prev => prev.filter(r => r.id !== res.id));
                              setRejectingId(null); setRejectReason('');
                            }}>Rechazar</Button>
                          <Button size="sm" variant="ghost" className="h-8 text-muted-foreground" onClick={() => { setRejectingId(null); setRejectReason(''); }}>
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 flex-wrap">
                          <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700"
                            onClick={async () => {
                              await confirmReservation(res.id);
                              setPendingRes(prev => prev.filter(r => r.id !== res.id));
                            }}>
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Confirmar
                          </Button>
                          <Button size="sm" variant="outline" className="h-8 border-border text-muted-foreground"
                            onClick={() => { setRejectingId(res.id); setRejectReason(''); }}>
                            <XCircle className="w-3.5 h-3.5 mr-1" />Rechazar
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 text-muted-foreground"
                            onClick={() => navigate(`/admin/events/${res.event}`)}>
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Action bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-3">
          <h2 className="text-lg sm:text-xl font-serif font-semibold text-foreground">Gestión de Eventos</h2>
          <div className="grid grid-cols-3 sm:flex sm:items-center gap-2 sm:gap-3">
            <Button data-testid="create-event-btn" onClick={() => navigate('/admin/events/new')} className="bg-primary hover:bg-accent text-xs sm:text-sm">
              <Plus className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Nuevo Evento</span><span className="sm:hidden">Nuevo</span>
            </Button>
            <Button data-testid="qr-scanner-btn" variant="outline" onClick={() => navigate('/admin/scanner')} className="border-border text-muted-foreground hover:text-foreground text-xs sm:text-sm">
              <ScanLine className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Escanear QR</span><span className="sm:hidden">QR</span>
            </Button>
            <Button data-testid="payment-methods-btn" variant="outline" onClick={() => navigate('/admin/payment-methods')} className="border-border text-muted-foreground hover:text-foreground text-xs sm:text-sm">
              <CreditCard className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Métodos de Pago</span><span className="sm:hidden">Pagos</span>
            </Button>
          </div>
        </div>

        {/* Events: cards on mobile, table on desktop */}
        <div className="block lg:hidden space-y-3">
          {events.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              busy={busyEventId === event.id}
              onClick={() => navigate(`/admin/events/${event.id}`)}
              onLayout={() => navigate(`/admin/events/${event.id}/layout`)}
              onEdit={() => navigate(`/admin/events/${event.id}/edit`)}
              onDelete={() => handleDelete(event.id, event.name)}
              onClone={() => handleClone(event.id)}
              onPublish={() => handlePublish(event.id)}
              onUnpublish={() => handleUnpublish(event.id)}
            />
          ))}
          {events.length === 0 && (
            <div className="bg-secondary border border-border rounded-lg p-8 text-center text-muted-foreground">No hay eventos creados.</div>
          )}
        </div>

        <div className="hidden lg:block bg-secondary border border-border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Evento</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Fecha</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Estado</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Sillas</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Precio desde</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {events.map((event) => {
                const statusConf = eventStatuses[event.status] || eventStatuses.open;
                const isDraft = event.status === 'draft';
                return (
                  <tr key={event.id} data-testid={`event-row-${event.id}`} className={`hover:bg-muted/30 transition-colors cursor-pointer ${isDraft ? 'opacity-70' : ''}`} onClick={() => navigate(`/admin/events/${event.id}`)}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <img src={event.image} alt={event.name} className="w-12 h-12 rounded object-cover" />
                        <div>
                          <p className="font-medium text-foreground hover:text-primary transition-colors">{event.name}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[200px]">{event.description}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-foreground">{formatDate(event.date)}</p>
                      <p className="text-xs text-muted-foreground">{event.time} hrs</p>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={statusConf.color}>{statusConf.label}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-foreground">{event.availableChairs}/{event.totalChairs}</p>
                      <p className="text-xs text-muted-foreground">disponibles</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-primary">{event.priceFrom > 0 ? formatPrice(event.priceFrom, event.currency) : '-'}</p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        {isDraft ? (
                          <Button variant="ghost" size="sm" data-testid={`publish-event-${event.id}`} disabled={busyEventId === event.id} onClick={() => handlePublish(event.id)} className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10">
                            <Send className="w-4 h-4 mr-1" /><span className="text-xs">Publicar</span>
                          </Button>
                        ) : (
                          <Button variant="ghost" size="sm" data-testid={`unpublish-event-${event.id}`} disabled={busyEventId === event.id} onClick={() => handleUnpublish(event.id)} className="text-zinc-400 hover:text-zinc-300 hover:bg-zinc-500/10" title="Pasar a borrador">
                            <EyeOff className="w-4 h-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" data-testid={`clone-event-${event.id}`} disabled={busyEventId === event.id} onClick={() => handleClone(event.id)} className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10" title="Duplicar">
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" data-testid={`edit-layout-${event.id}`} onClick={() => navigate(`/admin/events/${event.id}/layout`)} className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10" title="Editar layout">
                          <LayoutGrid className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" data-testid={`edit-event-${event.id}`} onClick={() => navigate(`/admin/events/${event.id}/edit`)} className="text-muted-foreground hover:text-foreground" title="Editar info">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" data-testid={`delete-event-${event.id}`} onClick={() => handleDelete(event.id, event.name)} className="text-destructive hover:text-destructive hover:bg-destructive/10" title="Eliminar">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {events.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">No hay eventos creados. ¡Crea tu primer evento!</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

const StatCard = ({ icon, bg, value, label }) => (
  <div className="bg-secondary border border-border rounded-lg p-3 sm:p-4">
    <div className="flex items-center gap-2 sm:gap-3">
      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full ${bg} flex items-center justify-center shrink-0`}>{icon}</div>
      <div className="min-w-0">
        <p className="text-lg sm:text-2xl font-bold text-foreground leading-tight truncate">{value}</p>
        <p className="text-[10px] sm:text-xs text-muted-foreground leading-tight">{label}</p>
      </div>
    </div>
  </div>
);

const EventCard = ({ event, busy, onClick, onLayout, onEdit, onDelete, onClone, onPublish, onUnpublish }) => {
  const statusConf = eventStatuses[event.status] || eventStatuses.open;
  const isDraft = event.status === 'draft';
  return (
    <div data-testid={`event-card-mobile-${event.id}`} className={`bg-secondary border border-border rounded-lg overflow-hidden ${isDraft ? 'opacity-80' : ''}`}>
      <div onClick={onClick} className="flex items-center gap-3 p-3 cursor-pointer">
        <img src={event.image} alt={event.name} className="w-14 h-14 rounded object-cover shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium text-foreground truncate">{event.name}</p>
            <Badge className={`${statusConf.color} text-[10px]`}>{statusConf.label}</Badge>
          </div>
          <p className="text-xs text-muted-foreground truncate">{formatDate(event.date)} · {event.time}</p>
          <p className="text-xs text-muted-foreground">{event.availableChairs}/{event.totalChairs} sillas · {formatPrice(event.priceFrom, event.currency)}</p>
        </div>
      </div>
      <div className="border-t border-border grid grid-cols-5 divide-x divide-border bg-background/50">
        {isDraft ? (
          <button data-testid={`publish-mobile-${event.id}`} disabled={busy} onClick={onPublish} className="py-2.5 flex items-center justify-center gap-1 text-emerald-400 hover:bg-emerald-500/10 text-xs">
            <Send className="w-3.5 h-3.5" /><span className="hidden xs:inline">Publicar</span>
          </button>
        ) : (
          <button data-testid={`unpublish-mobile-${event.id}`} disabled={busy} onClick={onUnpublish} className="py-2.5 flex items-center justify-center text-zinc-400 hover:bg-zinc-500/10" title="Pasar a borrador">
            <EyeOff className="w-3.5 h-3.5" />
          </button>
        )}
        <button data-testid={`clone-mobile-${event.id}`} disabled={busy} onClick={onClone} className="py-2.5 flex items-center justify-center text-purple-400 hover:bg-purple-500/10" title="Duplicar">
          <Copy className="w-3.5 h-3.5" />
        </button>
        <button onClick={onLayout} className="py-2.5 flex items-center justify-center text-blue-400 hover:bg-blue-500/10" title="Layout">
          <LayoutGrid className="w-3.5 h-3.5" />
        </button>
        <button onClick={onEdit} className="py-2.5 flex items-center justify-center text-muted-foreground hover:bg-muted" title="Editar">
          <Edit className="w-3.5 h-3.5" />
        </button>
        <button onClick={onDelete} className="py-2.5 flex items-center justify-center text-destructive hover:bg-destructive/10" title="Eliminar">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default AdminDashboard;
